<template>
  <main class="page-parent">
    <picture>
      <source
        type="image/webp"
        srcset="~@/assets/registerPage/chargingCar.webp"
      />
      <img class="hero-image" src="~@/assets/registerPage/chargingCar.jpg" />
    </picture>
    <article class="registration-container" data-state="registered">
      <!-- Registration Start -->

      <Form
        :errorMessage="errorMessage"
        :conditions="conditions"
        :registered="registered"
        :toggle="toggle"
        :togglePw="togglePw"
        @checkbox="reducer"
        :reducer="reducer"
      >
        <template v-slot:personalDetails>
          <TextInput
            :reducer="reducer"
            :data="name"
            :analyticsPermitted="analyticsPermitted"
          />
          <TextInput
            :reducer="reducer"
            :data="email"
            :analyticsPermitted="analyticsPermitted"
          />
        </template>
        <template v-slot:passwords>
          <div :data-open="toggle" class="pw-container">
            <TextInput
              :data="password"
              :icon="true"
              :reducer="reducer"
              :analyticsPermitted="analyticsPermitted"
            />
            <TextInput
              :data="repeatPassword"
              :icon="true"
              :reducer="reducer"
              :analyticsPermitted="analyticsPermitted"
            />
          </div>
        </template>
      </Form>

      <!-- Registration End -->

      <RegistrationSuccess
        :registered="registered"
        :registeredEmail="registeredEmail"
        :sendAgain="sendToBackend"
      />
    </article>
  </main>
</template>

<script>
import { countryCode } from '@/../region.json';
import TextInput from '../components/Registration/TextInput.vue';
import RegistrationSuccess from '../components/Registration/RegistrationSuccess.vue';
import Form from '../components/Registration/Form.vue';
import initialState from '../data/register';
import createFormData from '../components/Registration/shared';
import i18n from '../i18n';
import { sendAnalytics } from '../shared/analytics';
import { LOCALSTORAGE_IDS } from '../data/constants';
import {
  getRecaptchaToken,
  loadGoogleCaptcha,
  unloadGoogleCaptcha,
} from '../shared/recaptchaHelper';
import EmailUtils from '../shared/email';

export default {
  name: 'Register',
  components: { TextInput, RegistrationSuccess, Form },
  data() {
    return initialState;
  },
  created() {
    // run this to check if user has agreed to analytics
    this.checkLocalStorage();
  },
  mounted() {
    loadGoogleCaptcha();
  },
  beforeDestroy() {
    unloadGoogleCaptcha();
  },
  methods: {
    handleAnalytics(type) {
      if (!this.analyticsPermitted) {
        // user has denied analytics
        return;
      }
      switch (type) {
        case 'checkbox': {
          if (this.triggeredAnalytics.checkbox) {
            break;
          }

          sendAnalytics('tap_button', {
            title: 'signup:termsAndConditions',
          });
          this.triggeredAnalytics.checkbox = true;
          break;
        }
        case 'togglePassword': {
          if (this.triggeredAnalytics.togglePassword) {
            break;
          }

          sendAnalytics('tap_button', {
            title: 'signup:setUpPassword',
          });
          this.triggeredAnalytics.togglePassword = true;
          break;
        }
        case 'submit': {
          if (this.triggeredAnalytics.submit) {
            break;
          }

          sendAnalytics('tap_button', { title: 'signup:submit' });
          this.triggeredAnalytics.submit = true;
          break;
        }
        default:
      }
    },
    checkLocalStorage() {
      const decision = window.localStorage.getItem(
        LOCALSTORAGE_IDS.USER_COOKIES
      );

      if (decision !== null) {
        const parsedDecision = JSON.parse(decision);

        if (parsedDecision) {
          this.analyticsPermitted = true;
        }
      }
    },
    onFormSubmit() {
      // filter out passwords if user is not submitting a password
      const filterPasswordInputs = input =>
        this.toggle ? input : !input.label.startsWith('pass');
      // filteredInputs -> [name, email] or [name, email, password, repeatPassword]
      const textInputs = [
        this.email,
        this.name,
        this.password,
        this.repeatPassword,
      ].filter(filterPasswordInputs);

      const isValid = this.checkValidity(textInputs);
      if (!isValid) {
        return;
      }
      // form is valid -> try to submit to backend
      this.sendToBackend();
    },
    checkValidity(textInputs) {
      // returns true if all inputs are valid
      return textInputs
        .map(input =>
          this.validate({ event: input.label, payload: input.text })
        )
        .every(valid => valid);
    },
    validateActiveFields(currentField) {
      // filter out text inputs that have not been clicked
      const activeInputs = [
        this.name,
        this.email,
        this.password,
        this.repeatPassword,
      ].filter(input => input.clicked && input.label !== currentField);
      // for every active input, check if it is valid
      // if not then display an appropriate error message
      activeInputs.forEach(input =>
        this.validate({ event: input.label, payload: input.text })
      );
    },
    // this is where we handle child events from the text inputs
    // child sends event -> {event: 'name, payload: 'example payload'} in response we update the state 'redux' style
    reducer(action) {
      switch (action.event) {
        case 'name':
          this.name.text = action.payload;
          if (this.name.status === 'error') {
            this.name.error = '';
            this.name.status = 'default';
          }
          break;
        case 'email':
          this.email.text = action.payload;
          if (this.email.status === 'error') {
            this.email.error = '';
            this.email.status = 'default';
          }
          break;
        case 'password':
          this.password.text = action.payload;
          if (this.password.status === 'error') {
            this.password.error = '';
            this.password.status = 'default';
          }
          if (this.password.text === this.repeatPassword.text) {
            this.clearPasswordErrors();
          }
          break;
        case 'repeatPassword':
          this.repeatPassword.text = action.payload;
          if (this.repeatPassword.status === 'error') {
            this.repeatPassword.error = '';
            this.repeatPassword.status = 'default';
          }
          if (this.password.text === this.repeatPassword.text) {
            this.clearPasswordErrors();
          }
          break;
        case 'checkbox':
          this.conditions = !this.conditions;
          this.handleAnalytics('checkbox');
          break;
        case 'togglePassword':
          this.togglePw();
          this.handleAnalytics('togglePassword');
          break;
        case 'submit':
          this.onFormSubmit();
          this.handleAnalytics('submit');
          break;
        case 'clicked': {
          // text input has been clicked
          // check for validity
          this.validateActiveFields(action.payload);
          switch (action.payload) {
            case 'name':
              this.name.clicked = true;
              break;
            case 'email':
              this.email.clicked = true;
              break;
            case 'password':
              this.password.clicked = true;
              break;
            case 'repeatPassword':
              this.repeatPassword.clicked = true;
              break;
            default:
              break;
          }
          break;
        }

        default:
      }
    },
    validate({ event, payload }) {
      switch (event) {
        case 'name':
          return this.validateName(payload);

        case 'email':
          return this.validateEmail(payload);

        case 'password':
          return this.validatePassword();

        case 'repeatPassword':
          return this.validateRepeatPassword();

        default:
          return false;
      }
    },
    // keep
    clearPasswordErrors() {
      this.repeatPassword.status = 'default';
      this.repeatPassword.error = '';
      this.password.status = 'default';
      this.password.error = '';
    },
    // keep
    togglePw() {
      if (this.toggle) {
        this.clearPasswords();
      }
      this.toggle = !this.toggle;
    },

    async sendToBackend() {
      try {
        const recaptchaToken = await getRecaptchaToken('REGISTER');

        const formData = createFormData({
          email: this.email.text,
          name: this.name.text,
          password: this.password.text,
          currentUrl: this.$route.path,
          recaptchaToken,
        });

        const res = await fetch(
          `${process.env.VUE_APP_SERVICES_URL}/register`,
          {
            method: 'post',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded',
              'x-mobile-app-origin-country-code': countryCode,
            },
            body: formData,
          }
        );
        switch (res.status) {
          case 200:
            return this.userRegister(res);
          case 400:
            return this.missingField(res);
          case 409:
            return this.alreadySignedUp(res);
          default:
            return this.showUnknownError(res);
        }
      } catch {
        this.showUnknownError();
        return null;
      }
    },

    showUnknownError() {
      this.errorMessage = i18n.t('signup.error_unknown');
    },
    async missingField(res) {
      const body = await res.json();
      switch (body.msg_key) {
        case 'response_api_err_register_missing_given_name':
          this.errorMessage = i18n.t('signup.input_name_error');
          return;
        case 'response_api_err_register_invalid_language':
          this.errorMessage = i18n.t('signup.error_unknown');
          return;
        case 'response_api_err_register_invalid_email':
          this.errorMessage = i18n.t('signup.input_email_error');
          return;
        default:
          this.errorMessage = i18n.t('signup.error_unknown');
      }
    },
    async alreadySignedUp(res) {
      const body = await res.json();
      if (body.msg_key === 'response_api_err_register_email_duplicate') {
        this.email.error = i18n.t('signup.input_email_error_registered');
        this.email.status = 'error';
      }
    },

    async userRegister(res) {
      const body = await res.json();
      this.registeredEmail = body.data.email;
      this.registered = true;
      this.errorMessage = null;
    },

    clearPasswords() {
      this.password = {
        ...this.password,
        text: '',
        error: '',
        status: 'default',
        clicked: false,
      };
      this.repeatPassword = {
        ...this.repeatPassword,
        text: '',
        error: '',
        status: 'default',
        clicked: false,
      };
    },
    validateName(payload) {
      if (payload.length < 2) {
        this.name.status = 'error';
        this.name.error = i18n.t('signup.input_name_error');
        return false;
      }
      this.name.status = 'default';
      this.name.error = '';
      return true;
    },
    validateEmail(payload) {
      if (!EmailUtils.emailIsValid(payload)) {
        this.email.error = i18n.t('signup.input_email_error');
        this.email.status = 'error';
        return false;
      }
      if (this.email.status === 'error') {
        this.email.status = 'default';
        this.email.error = '';
      }
      return true;
    },
    validatePassword() {
      if (this.toggle) {
        if (
          this.repeatPassword.text !== this.password.text &&
          this.repeatPassword.text.length > 0
        ) {
          this.password.error = i18n.t('signup.input_password_error_match');
          this.password.status = 'error';
          return false;
        }
      }
      if (this.password.text.length < 8) {
        this.password.error = i18n.t('signup.input_password_error_small');
        this.password.status = 'error';
        return false;
      }
      if (this.password.status === 'error') {
        this.clearPasswordErrors();
      }
      return true;
    },
    validateRepeatPassword() {
      if (this.toggle) {
        if (
          this.password.text !== this.repeatPassword.text &&
          this.password.text.length > 0
        ) {
          this.repeatPassword.error = i18n.t(
            'signup.input_password_error_match'
          );
          this.repeatPassword.status = 'error';
          return false;
        }
        if (this.repeatPassword.text.length < 8) {
          this.repeatPassword.error = i18n.t(
            'signup.input_password_error_small'
          );
          this.repeatPassword.status = 'error';
          return false;
        }
        if (this.repeatPassword.status === 'error') {
          this.clearPasswordErrors();
        }
      }
      return true;
    },
  },
};
</script>

<style scoped>
/*
======================
       MOBILE
======================
*/
.page-parent {
  padding-top: 76px;
  display: grid;
  grid-template-columns: 1fr;
  min-height: 100vh;
  --fin-text-padding: 0.5em;
  --mail-icon-height: 110px;
  --download-image-width: 40vw;
  --register-form-p: 50px;
}

.hero-image-container {
  display: block;
}
.hero-image {
  display: none;
}

.registration-container {
  display: grid;
  place-items: center;
}

/* pw container */

.pw-container {
  opacity: 0;
  max-height: 0px;
  transition: max-height 0.3s ease-in-out, opacity 0.1s linear 0.06s;
  visibility: hidden;
}
.pw-container[data-open='true'] {
  max-height: 200px;
  opacity: 1;
  visibility: visible;
  transition: max-height 0.25s ease-in-out, opacity 0.1s linear 0.1s;
}

/*
======================
       DESKTOP
======================
*/

@media (min-width: 1024px) {
  .page-parent {
    grid-template-columns: 1.3fr 1fr;
    --register-form-p: 100px;
  }
  .hero-image {
    display: block;
    object-fit: cover;
    width: 100% !important;
    height: 100% !important;
  }
  .page-parent {
    --fin-text-padding: 0.7em;
    --mail-icon-height: auto;
    --download-image-width: auto;
  }
}
</style>
